import { uuid } from "config/app-constants";
import { AppContextManager } from "config/AppContext";
import { DatalistDataSourceFilterDefinitionType, DatalistDataSourceFilterType, DatalistTableColumnType } from "types/datalist-types";
import StringUtilities from "../common-utilities/string-utilities";
import { DateUtil } from "../date-utilities";

export enum Conditions {
    null = 'null',
    nnull = 'nnull',
    eq = "eq",
    neq = "neq",
    in = "in",
    nin = "nin",
    contains = 'contains',
    endsWith = 'endsWith',
    ncontains = 'ncontains',
    nendsWith = 'nendsWith',
    nstartsWith = 'nstartsWith',
    startsWith = 'startsWith',
    gt = 'gt',
    gte = 'gte',
    lt = 'lt',
    lte = 'lte',
    ngt = 'ngt',
    ngte = 'ngte',
    nlt = 'nlt',
    nlte = 'nlte',
    between = 'between',
    nbetween = 'between',
}

export type NullCondition = Conditions.null | Conditions.nnull;

export type EqualCondition = Conditions.eq | Conditions.neq;

export type InCondition = Conditions.in | Conditions.nin;

export type StringCondition = EqualCondition | InCondition | Conditions.contains | Conditions.endsWith | Conditions.ncontains | Conditions.nendsWith | Conditions.nstartsWith | Conditions.startsWith;

export type NullStringCondition = StringCondition | NullCondition;

export type ComparableCondition = EqualCondition | InCondition | Conditions.gt | Conditions.gte | Conditions.lt | Conditions.lte | Conditions.ngt | Conditions.ngte | Conditions.nlt | Conditions.nlte | Conditions.between | Conditions.nbetween;

export type NullComparableCondition = ComparableCondition | NullCondition;

export const stringCoreFilterConditions: StringCondition[] = [
    Conditions.contains,
    Conditions.endsWith,
    Conditions.ncontains,
    Conditions.nendsWith,
    Conditions.nstartsWith,
    Conditions.startsWith,
];

export const nullableFilterConditions: Conditions[] = [
    Conditions.null,
    Conditions.nnull
];

export const equalFilterConditions: EqualCondition[] = [
    Conditions.eq,
    Conditions.neq
];

export const nullFilterConditions: NullCondition[] = [
    Conditions.null,
    Conditions.nnull
];

export const stringFilterConditions: StringCondition[] = [
    ...equalFilterConditions,
    ...stringCoreFilterConditions,
];

export const comparableCondition: ComparableCondition[] = [
    ...equalFilterConditions,
    Conditions.gt,
    Conditions.gte,
    Conditions.lt,
    Conditions.lte,
    Conditions.ngt,
    Conditions.ngte,
    Conditions.nlt,
    Conditions.nlte,
];

export const filterConditions = {
    string: stringFilterConditions as Conditions[],
    nullString: [
        ...nullableFilterConditions,
        ...stringFilterConditions
    ] as Conditions[],
    number: comparableCondition as Conditions[],
    nullNumber: [
        ...nullableFilterConditions,
        ...comparableCondition
    ] as Conditions[],
    reference: stringFilterConditions as Conditions[],
    nullReference: [
        ...nullableFilterConditions,
        ...stringFilterConditions
    ] as Conditions[],
    date: comparableCondition as Conditions[],
    nullDate: [
        ...nullableFilterConditions,
        ...comparableCondition
    ] as Conditions[],
    dateOnly: comparableCondition as Conditions[],
    nullDateOnly: [
        ...nullableFilterConditions,
        ...comparableCondition
    ] as Conditions[],
    boolean: equalFilterConditions as Conditions[],
    nullBoolean: [
        ...nullableFilterConditions,
        ...equalFilterConditions
    ] as Conditions[],
};

// #region functions

export function resolveFilterInput<T, TFilter>(filterString: string, columns: DatalistTableColumnType<T, TFilter, any>[], defaultFilters: DatalistDataSourceFilterType | undefined): (TFilter | undefined) {
    let filter = defaultFilters ? { ...defaultFilters } : undefined;
    if (filterString) {
        filter = StringUtilities.parseValue(filterString) as DatalistDataSourceFilterType;
        if (!filter) return undefined;
    }
    if (!filter) return undefined;

    return resolveDatalistDataSourceFilter(filter, columns);
}

function resolveDatalistDataSourceFilter<T, TFilter>(filter: DatalistDataSourceFilterType | DatalistDataSourceFilterDefinitionType, columns: DatalistTableColumnType<T, TFilter, any>[]): (TFilter | undefined) {
    if (!filter) return undefined;
    if ('filters' in filter) {
        const filters = filter.filters.map(f => resolveDatalistDataSourceFilter(f, columns));
        let model = {} as any;
        model[filter.logic] = filters.filter(f => f !== undefined);
        return model as TFilter;
    } else {
        const column = columns.find(c => c.name === filter.name);
        if (!column) return undefined;
        const type = column.filter?.type || 'string';
        const condition = filter.condition as Conditions;
        const value = filter.value;
        return column.filter?.resolve ? column.filter.resolve(filter) : setFilterInput<TFilter>(column.name, condition, value, type, column.filter?.path);
    }
}

export function setFilterInput<T>(field: string, condition: Conditions, value: any, type?: keyof typeof filterConditions, path?: string[]) {
    let filter: any = {};
    if (type === 'reference') {
        filter = resolveReferenceValue(field, condition as NullStringCondition, value, path);
    } else if (type?.includes('null') && condition === Conditions.null) {
        filter[field] = {};
        filter[field][Conditions.eq] = null;
    } else if (type?.includes('null') && condition === Conditions.nnull) {
        filter[field] = {};
        filter[field][Conditions.neq] = null;
    } else {
        filter[field] = {};
        filter[field][condition] = getValueAsType(value, type);
    }

    return filter as T;
}

function getValueAsType(value: any, type?: keyof typeof filterConditions) {
    switch (type) {
        case "number":
        case "nullNumber":
            const numValue = Number(value);
            if (isNaN(numValue) || numValue === undefined) return null;
            return numValue;
        case "dateOnly":
        case "nullDateOnly":
            return DateUtil.toIsoDate(value);
        default:
            return value;
    }
}

function resolveReferenceValue(field: string, condition: NullStringCondition, value: any, path?: string[]) {
    let filter = {} as any;
    if (path?.length) {
        let obj = filter;
        path.forEach((p, i) => {
            obj[p] = {};
            obj = obj[p];
        });

        if (typeof value == 'object' && 'id' in value) {
            obj.id = {};
            obj.id[condition] = value.id;
        } else if (typeof value === 'number') {
            obj.id = {};
            obj.id[condition] = value;
        } else {
            let nameFilter = {
                name: {
                    [condition]: value.toString()
                }
            };

            let normalizedObj = {
                normalizedName: {
                    [condition]: resolveNormalizedNameValue(value, condition)
                }
            };

            obj.or = [
                nameFilter,
                normalizedObj
            ];
        }
    } else {
        let obj: any = filter;

        if (typeof value == 'object' && 'id' in value) {
            obj[field + "Id"] = {};
            obj[field + "Id"][condition] = value.id;
        } else if (typeof value === 'number') {
            obj[field + "Id"] = {};
            obj[field + "Id"][condition] = value;
        } else {
            let namefilter = {
                name: {
                    [condition]: value.toString()
                }
            };

            let normalizedObj = {
                [field]: {
                    normalizedName: {
                        [condition]: resolveNormalizedNameValue(value, condition)
                    }
                }
            };

            obj.or = [
                namefilter,
                normalizedObj
            ];
        }
    }

    return filter;
}
// #endregion functions

// #region value input
export function appendFilters<T,>(baseFilter: T | undefined | null, filter: T, condition: 'and' | 'or' = 'and'): T {
    if (!baseFilter) return filter;
    return {
        [condition]: [
            baseFilter,
            filter
        ]
    } as T
}
// #endregion value input


// #region  filed type definition
export class DatalistFilterDefinitions {
    static seasonFilterDefinition(context: AppContextManager): DatalistDataSourceFilterDefinitionType {
        return {
            condition: Conditions.eq,
            name: 'season',
            value: {
                name: context.t('common.active'),
                id: -1
            },
            id: uuid(),
        };
    }
}
// #endregion  filed type definition

export function resolveSearchFilterProperty(propertyPath: string[], value: any, condition: Conditions = Conditions.contains) {
    let filter = {} as any;
    let currentFilter = filter;
    for (let i = 0; i < propertyPath.length; i++) {
        const property = propertyPath[i];
        currentFilter[property] = {};
        if (i === propertyPath.length - 1) {
            currentFilter[property][condition] = value;
        } else {
            currentFilter[property] = {};
            currentFilter = currentFilter[property];
        }
    }

    return filter;
}

export function resolveSearchFilterPropertyName(propertyPath: string[], value: any, condition: Conditions = Conditions.contains) {
    propertyPath = propertyPath || [];
    var nameFilter = resolveSearchFilterProperty([...propertyPath, 'name'], value, condition);
    var normalizedNameFilter = resolveSearchFilterProperty([...propertyPath, 'normalizedName'], resolveNormalizedNameValue(value, condition), condition);

    return {
        or: [
            nameFilter,
            normalizedNameFilter
        ]
    }
}

export function resolveSearchFilterPropertyNames(propertyPath: string[][], value: any, condition: Conditions = Conditions.contains) {
    return {
        or: propertyPath.map(path => resolveSearchFilterPropertyName(path, value, condition))
    };
}

export function resolveNormalizedNameValue(value: string, condition: Conditions = Conditions.contains) {
    let normalizedNameValue = value?.toLowerCase() || '';
    switch (condition) {
        case Conditions.startsWith:
        case Conditions.nstartsWith:
            normalizedNameValue = '|' + normalizedNameValue;
            break;
        case Conditions.endsWith:
        case Conditions.nendsWith:
            normalizedNameValue = normalizedNameValue + '|';
            break;
        case Conditions.eq:
        case Conditions.neq:
            normalizedNameValue = '|' + normalizedNameValue + '|';
            break;
    }

    return normalizedNameValue;
}
